import { useMutation, useQuery } from "@apollo/client/react"; import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Box, Grid } from "@mui/material";
import CardBox from "../../CardBox/CardBox";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_VALORACION_CP, { adaptAddPresencialValoracionCPInput } from "../../../../../../data/graphQL/mutations/Visitas/saveValoracionCP";
import ES_ENFERMERO_CUIDADOR from "../../../../../../data/graphQL/queries/MisVisitas/esEnfermeroCuidadorAuxiliar";
import { useStepSave } from "../../../../hooks/useStepSave";

import { ID_TIPO_VISITA_PRESENCIAL } from "../../../MisVisitasCreationContent";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { GET_COMBO_VISITA_VALOR, adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import CalculadoraESAS from "../../../../../ResumenIngresoCreation/components/StepCuidadosPaliativos/components/CalculadoraESAS";

const ID_COMBO_ECOG = 6;
const ID_COMBO_KARNOFSKY = 7;

export default function ValoracionCP({
  esUrgencia,
  readOnly,
  onSaveRef,
  onExposeFormatted,
  programa,
  esEnfermeroCuidador,
  setEsEnfermeroCuidador
}) {
  const [formData, setFormData] = useState({});
  const { visita, setVisita } = useVisita();
  const [karnofskyOptions, setKarnofskyOptions] = useState([]);
  const [ecogOptions, setEcogOptions] = useState([]);

  const { data: dataEnfermeroCuidador } = useQuery(ES_ENFERMERO_CUIDADOR, {
    fetchPolicy: 'no-cache',
    variables: { idVisita: visita?.idVisita }
  });

  useEffect(() => {
    if (!dataEnfermeroCuidador) return;
    setEsEnfermeroCuidador(dataEnfermeroCuidador?.esEnfermeroCuidador);
  }, [dataEnfermeroCuidador]);

  const [save] = useMutation(SAVE_VALORACION_CP);

  const { data: dataComboVisitaValor } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_ECOG }
  });

  useEffect(() => {
    if (!dataComboVisitaValor) return;
    setEcogOptions(adaptCombosVisitas(dataComboVisitaValor));
  }, [dataComboVisitaValor]);

  const { data: dataComboVisitaValorKar } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_KARNOFSKY }
  });

  useEffect(() => {
    if (!dataComboVisitaValorKar) return;
    setKarnofskyOptions(adaptCombosVisitas(dataComboVisitaValorKar));
  }, [dataComboVisitaValorKar]);

  const isNoProgramada = formData?.programada === "no";
  const isDiuresisPositiva = formData.diuresis === "Positiva";

  const handleChange = useCallback((name, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value
      };

      if (name === 'escaras' && value === false) {
        newData.estadio = null;
      }

      return newData;
    });
  }, []);

  const formattedInputs = useMemo(() => {
    return inputs.map((input) => {
      const newInput = { ...input };

      if (isNoProgramada) {
        newInput.required = false;
      }

      if (newInput.name === 'estadio') {
        newInput.required = formData.escaras === true;
      }

      if (newInput.name === "cantidadDiuresis") {
        newInput.hidden = !formData.diuresis;
        const newValueCantidad = !!formData.diuresis ? formData.cantidadDiuresis : null;
        handleChange("cantidadDiuresis", newValueCantidad);
      }
      if (newInput.name === "cantidad") {
        handleChange("cantidad", null);
        newInput.disabled = !isDiuresisPositiva;
      }
      if (newInput.name === "karnofsky") newInput.options = karnofskyOptions;
      if (newInput.name === "eCOG") newInput.options = ecogOptions;

      if (newInput.name === 'caracteristicas') newInput.required = !!formData?.catarsis;

      if (esEnfermeroCuidador && !!newInput?.noEsParaCuidadorOAuxiliar)
        newInput.hidden = true;

      if (esUrgencia && newInput.name !== 'catarsis') newInput.required = false;

      if (readOnly) newInput.disabled = true;

      return newInput;
    });
  }, [
    readOnly,
    esUrgencia,
    isNoProgramada,
    isDiuresisPositiva,
    formData.diuresis,
    esEnfermeroCuidador,
    karnofskyOptions,
    ecogOptions,
    formData.escaras,
    formData.catarsis
  ]
  );

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedInputs, formData });

    const { data } = await save({ variables: { input: adaptAddPresencialValoracionCPInput(formData) } });

    const { idPresencialCPCD: idPresencialCPCDNuevo, idESAS } = data?.saveVisitaPresencialCPValoracion || {};
    if (!idPresencialCPCDNuevo) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData,
      idPresencialCPCD: idPresencialCPCDNuevo,
      idESAS: idESAS
    }));

  });

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'valoracion',
      stepName: 'Valoración',
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  useEffect(() => {
    if (visita.tipoVisita === ID_TIPO_VISITA_PRESENCIAL && (programa === "AE" || programa === "DI") && !!onSaveRef) {
      toast('OJO! Este combinacion de programa + tipo de visita aún se encuentra en desarrollo!', {
        icon: '⚠️'
      });
    }
  }, [visita.tipoVisita, onSaveRef]);

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) =>
            <>
              {input.name !== 'resultadoESAS' &&
                input.name !== 'cantidadDiuresis' &&
                <Grid size={{ xs: 12 }} key={input.name}>
                  <RenderInputByTypeMobile
                    key={`${input.name}-${input.type}${input.type === 'multiple_boolean' ? formData[input.name] : ''}`}
                    input={input}
                    value={formData[input.name]}
                    handleChange={handleChange}
                    row={input.type === 'multiple_boolean'} />

                </Grid>
              }
              {input.name === 'cantidadDiuresis' &&
                <Grid size={{ xs: 12 }} key={input.name} sx={{ display: 'row' }}>
                  <Grid size={{ xs: 6 }} key={`${input.name}-${input.type}`}>
                    <RenderInputByTypeMobile
                      key={`${input.name}-${input.type}${input.type === 'multiple_boolean' ? formData[input.name] : ''}`}
                      input={input}
                      value={formData[input.name]}
                      handleChange={handleChange}
                      row={input.type === 'multiple_boolean'} />

                  </Grid>
                  <Grid size={{ xs: 6 }} key={`${input.name}-${input.type}`} sx={{ marginLeft: '1rem' }}>
                    <strong>ml</strong>
                  </Grid>
                </Grid>
              }
              {input.name === 'resultadoESAS' && (
                <CalculadoraESAS
                  variant="esasVisita"
                  handleChange={handleChange}
                  input={input}
                  data={formData}
                  prevResult={formData[input.name]}
                  disabledModal={readOnly}
                />
              )}
            </>
          )}
      </Grid>
    </CardBox>);

}

const diuresisOpcs = [
  { value: true, label: "Positiva" },
  { value: false, label: "Negativa" }];


const inputs = [
  {
    name: "eCOG",
    displayName: "ECOG",
    type: "select",
    noEsParaCuidadorOAuxiliar: true,
    required: false
  },
  {
    name: "karnofsky",
    displayName: "Karnofsky",
    type: "select",
    noEsParaCuidadorOAuxiliar: true,
    required: false
  },
  {
    name: "resultadoESAS",
    displayName: "Resultado ESAS",
    type: "string",
    noEsParaCuidadorOAuxiliar: true,
    required: true
  },
  {
    name: 'escaras',
    displayName: 'Escaras',
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "estadio",
    displayName: "Estadío",
    type: "select",
    options: [
      { id: 1, displayName: "1" },
      { id: 2, displayName: "2" },
      { id: 3, displayName: "3" },
      { id: 4, displayName: "4" }
    ],

    noEsParaCuidadorOAuxiliar: true,
    required: true
  },
  {
    name: "diuresis",
    displayName: "Diuresis",
    type: "multiple_boolean",
    options: diuresisOpcs,
    required: true,
    row: true,
    lightTitle: true
  },
  {
    name: "cantidadDiuresis",
    displayName: "Cantidad",
    type: "int",
  },
  {
    name: 'catarsis',
    displayName: 'Catarsis',
    type: "multiple_boolean",
    options: diuresisOpcs,
    required: true,
    row: true,
    lightTitle: true
  },
  {
    name: "caracteristicas",
    displayName: "Características",
    type: "long_string",
    maxLength: 250
  }];